import { ACCESS_LEVELS, atLeastLevel } from '@/shared/access';
import type { AccessDomain, AccessLevel, CapabilityGrant, RiskClass, SubjectGrants } from '@/shared/access';

/**
 * What a department actually confers — the question an access review has to answer per department.
 *
 * <p>The review screen used to be able to answer it only per *person*. "Should Maria still have this?" is
 * the wrong unit for the quarterly sit-down: rights are attached to departments, so a department nobody
 * has looked at since it was created is where drift accumulates, and it is invisible in a per-person
 * matrix.
 */

/** Domain level plus capability exceptions, resolved the way the backend resolves them. */
export interface DepartmentRights {
  /** Domains this department confers something in, strongest first, then alphabetically. */
  domains: Array<{ domainCode: string; level: AccessLevel }>;
  /** True when it confers `ADMIN` — authority over how the company works, not just its data. */
  administers: boolean;
  /**
   * Granted capabilities classed `SECRET` / `FINANCIAL` / `GOVERNANCE` — the ones adr-auth-004 binds to a
   * step-up. Surfaced because "this department can move money" is the single most review-worthy fact
   * about it, and it is not visible from the domain level alone.
   */
  elevated: Array<{ domainCode: string; capabilityCode: string; risk: RiskClass }>;
  /** True when the department grants nothing at all. */
  empty: boolean;
}

/** The risk classes adr-auth-004 requires a fresh authentication (ACR step-up) to exercise. */
export const ELEVATED_RISKS: readonly RiskClass[] = ['SECRET', 'FINANCIAL', 'GOVERNANCE'];

function isElevated(risk: RiskClass): boolean {
  return ELEVATED_RISKS.includes(risk);
}

function exceptionFor(
  capabilities: CapabilityGrant[] | undefined,
  domainCode: string,
  capabilityCode: string,
): CapabilityGrant | undefined {
  return capabilities?.find(
    (grant) => grant.domainCode === domainCode && grant.capabilityCode === capabilityCode,
  );
}

/**
 * Whether a subject holding `level` in a domain ends up with a capability.
 *
 * <p>Mirrors `web-access-service`'s `AccessResolver` exactly, and the order is the whole rule:
 * <ol>
 *   <li>an explicit <b>DENY</b> wins unconditionally — it outranks the level and any allow;</li>
 *   <li>otherwise the level includes it when it reaches the capability's minimum;</li>
 *   <li>otherwise an explicit <b>ALLOW</b> grants it below that minimum — but only to a subject who can
 *       at least <i>see</i> the domain, so a capability can never become a side door into data the
 *       subject has no access to.</li>
 * </ol>
 * Getting this wrong in either direction misleads a reviewer: a missed capability hides real authority,
 * and an invented one gets rights revoked that were never held.
 */
export function capabilityGranted(
  level: AccessLevel,
  minimumLevel: AccessLevel,
  exception: CapabilityGrant | undefined,
): boolean {
  if (exception?.effect === 'DENY') {
    return false;
  }
  if (atLeastLevel(level, minimumLevel)) {
    return true;
  }
  return exception?.effect === 'ALLOW' && atLeastLevel(level, 'VIEW');
}

/** Resolves one department's stored grants against the platform catalogue. */
export function departmentRights(
  grants: SubjectGrants | undefined,
  catalogueDomains: AccessDomain[],
): DepartmentRights {
  const levels = grants?.levels ?? {};

  const domains = Object.entries(levels)
    .filter(([, level]) => Boolean(level) && level !== 'NONE')
    .map(([domainCode, level]) => ({ domainCode, level }))
    .sort(
      (a, b) =>
        ACCESS_LEVELS.indexOf(b.level) - ACCESS_LEVELS.indexOf(a.level) ||
        a.domainCode.localeCompare(b.domainCode),
    );

  const elevated: DepartmentRights['elevated'] = [];
  for (const domain of catalogueDomains) {
    const level = levels[domain.code] ?? 'NONE';
    for (const capability of domain.capabilities) {
      if (!isElevated(capability.risk)) {
        continue;
      }
      const exception = exceptionFor(grants?.capabilities, domain.code, capability.code);
      if (capabilityGranted(level, capability.minimumLevel, exception)) {
        elevated.push({
          domainCode: domain.code,
          capabilityCode: capability.code,
          risk: capability.risk,
        });
      }
    }
  }

  return {
    domains,
    administers: domains.some((entry) => entry.level === 'ADMIN'),
    elevated,
    empty: domains.length === 0 && elevated.length === 0,
  };
}
